import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Form } from 'antd';
import { Modal } from 'ui';
import { Input, Select } from 'components/Form';
import { useLecture } from "hooks/Lectures/useLecture";
import apiClient from "../../../../utils/apiClient";
import { config } from "../../../../config/config";
import useHandleError from "../../../../utils/useHandleError";

const formItemLayout = {
    labelCol: { span: 8 },
    wrapperCol: { span: 16 },
};

interface LectureCreationModaltInterface {
    programId?: number  | undefined
    visible?: boolean | undefined;
    onCancel(cancel: boolean): any;
    onSubmit(id: number): any;
}

const LectureCreationModal: React.FC<LectureCreationModaltInterface> = ({ programId, visible = false, onCancel, onSubmit }) => {
    const { saveLecture, lectureLoading } = useLecture();
    const intl = useIntl();
    const [form] = Form.useForm();
    const [handleError] = useHandleError();
    const [languageOptions, setLanguageOptions] = useState<Record<string, string>>({});

    useEffect(() => {
        const loadLanguages = async () => {
            try {
                const response = await apiClient.request('/api/v1/language', {}, 'GET');
                const options: Record<string, string> = {};
                Object.values(response?.languages ?? {}).forEach((l: any) => {
                    options[l.code] = l.code;
                });
                setLanguageOptions(options);
            } catch (error) {
                handleError(error);
            }
        };

        loadLanguages();
    }, []);

    useEffect(() => {
        if (visible) {
            form.setFieldsValue({ language: config.defaultEnvironmentLocale });
        }
    }, [visible]);

    const handleOnCancel = () => {
        form.resetFields();
        onCancel(false)
    }

    const handleFormSubmit = async (values: any) => {
        const selectedLanguage: string = values.language ?? config.defaultEnvironmentLocale;

        const payload: any = {
            title: values.title,
            titleTranslations: { [selectedLanguage]: values.title },
            descriptionTranslations: {},
        };

        if (!!programId) {
            payload.programId = programId;
        }

        const lecture = await saveLecture(payload);

        if (lecture && lecture.id) {
            form.resetFields();
            onSubmit(lecture.id);
        }
    };

    return (
        <Modal
            open={visible}
            isLoading={lectureLoading}
            onCancel={() => handleOnCancel()}
            title={intl.formatMessage({ id: 'general.lectures.add_new_lecture' })}
            okText={intl.formatMessage({ id: 'general.create' })}
            cancelText={intl.formatMessage({ id: 'general.cancel' })}
            onOk={form.submit}
        >
            <Form form={form} onFinish={handleFormSubmit} {...formItemLayout}>
                <Select
                    name='language'
                    label={intl.formatMessage({ id: 'general.language' })}
                    manualOptions={languageOptions}
                    customRules={[{ required: true, message: intl.formatMessage({ id: 'validation.field_required' }) }]}
                    customLayout={true} {...formItemLayout}
                />
                <Input
                    name='title'
                    label={intl.formatMessage({ id: 'general.lectures.title' })}
                    customRules={[{ required: true, message: intl.formatMessage({ id: 'validation.field_required' }) }]}
                    customLayout={true} {...formItemLayout}
                />
            </Form>
        </Modal>
    )
};
export default LectureCreationModal;
